import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { DynamicPage } from '../types';
import { useAuth } from '../context/AuthContext';
import Button from '../components/UI/Button';
import { Edit, Save, X, Trash2 } from 'lucide-react';
import PageEditor from '../components/Admin/PageEditor';

const DynamicPageViewer: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [page, setPage] = useState<DynamicPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  const fetchPage = async () => {
    setLoading(true);
    if (slug) {
      const p = await api.getPageBySlug(slug);
      setPage(p || null);
      if (p) {
        api.trackEvent('page_view', p.id, p.title);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const handleSave = async () => {
     // Reload page data after save
     await fetchPage();
     setIsEditing(false);
  };

  const handleDelete = async () => {
      if (window.confirm('آیا از حذف این صفحه اطمینان دارید؟ این عملیات غیرقابل بازگشت است.')) {
          if (page) {
              await api.deletePage(page.id);
              navigate('/');
          }
      }
  };

  if (loading) return <div className="p-20 text-center">در حال بارگذاری...</div>;

  // Editing Mode
  if (isEditing && page) {
      return (
          <div className="max-w-5xl mx-auto py-8 px-4">
              <PageEditor 
                  initialData={page} 
                  onSave={handleSave} 
                  onCancel={() => setIsEditing(false)} 
              />
          </div>
      );
  }

  // 404 State
  if (!page) {
    return (
      <div className="max-w-4xl mx-auto py-20 px-4 text-center">
        <h1 className="text-4xl font-black text-gray-300 mb-4">۴۰۴</h1>
        <p className="text-gray-600 text-lg">صفحه مورد نظر یافت نشد.</p>
        <button onClick={() => navigate('/')} className="mt-8 text-teal-600 hover:underline">بازگشت به صفحه اصلی</button>
      </div>
    );
  }

  const isAdmin = user?.role === 'admin';

  // Determine Container Class based on Layout
  const getContainerClass = () => {
      switch(page.layout) {
          case 'full': return 'max-w-7xl';
          case 'article': return 'max-w-2xl';
          default: return 'max-w-4xl';
      }
  };

  return (
    <div className="bg-white min-h-screen relative pb-16">
       
       {/* Admin Toolbar */}
       {isAdmin && (
        <div className="fixed bottom-6 left-6 z-50 flex gap-2 animate-fadeIn">
            <Button onClick={() => setIsEditing(true)} className="shadow-xl bg-gray-900 hover:bg-gray-800 flex items-center gap-2">
                <Edit size={18}/> ویرایش صفحه
            </Button>
            <Button onClick={handleDelete} variant="danger" className="shadow-xl flex items-center gap-2">
                <Trash2 size={18}/>
            </Button>
        </div>
      )}

       {/* Hero / Header */}
       <div className="bg-gray-900 text-white py-16">
          <div className={`${getContainerClass()} mx-auto px-4 text-center`}>
             <h1 className="text-3xl md:text-5xl font-black mb-4">{page.title}</h1>
             {page.description && <p className="text-gray-400 text-lg max-w-2xl mx-auto">{page.description}</p>}
          </div>
       </div>

       <div className={`${getContainerClass()} mx-auto px-4 py-12 space-y-10`}>
          {page.blocks.map(block => (
             <div key={block.id}>
                {block.type === 'header' && (
                   <h2 className="text-2xl font-bold text-gray-900 border-b pb-2 mb-4">{block.content}</h2>
                )}
                
                {block.type === 'text' && (
                   <div className="text-gray-700 leading-8 whitespace-pre-wrap text-lg text-justify">
                      {block.content}
                   </div>
                )}
                
                {block.type === 'image' && (
                   <div className="my-6">
                      <img src={block.content} alt={block.meta || 'تصویر'} className="w-full rounded-2xl shadow-lg" />
                      {block.meta && <p className="text-center text-sm text-gray-500 mt-2">{block.meta}</p>}
                   </div>
                )}

                {block.type === 'video' && (
                   <div className="my-6">
                       <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-lg relative">
                          {block.content.includes('aparat.com') || block.content.includes('youtube.com') || block.content.includes('embed') ? (
                             <iframe 
                                src={block.content} 
                                className="w-full h-full" 
                                frameBorder="0" 
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                allowFullScreen
                             ></iframe>
                          ) : (
                             <video src={block.content} controls className="w-full h-full"></video>
                          )}
                       </div>
                   </div>
                )}
             </div>
          ))}
       </div>
    </div>
  );
};

export default DynamicPageViewer;